import {
  AppShell,
  AppShellContent,
  Button,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { WorkHeader } from "../components/navigation/WorkHeader";
import { WorkSidebar } from "../components/navigation/WorkSidebar";
import { WorkAccessGate } from "../components/WorkAccessGate";
import {
  useWorkScope,
  WorkScopeProvider,
} from "../components/WorkScopeProvider";
import {
  canAccessWebNavigationModule,
  defaultPathForRole,
  fallbackPathForRole,
  moduleForPath,
  redirectForRoleChange,
} from "../lib/access";
import {
  type AssetTreeSearch,
  assetTreeSearchSchema,
} from "../lib/assetTreeSearch";
import {
  type NavigationOriginSearch,
  parseNavigationOrigin,
  toOriginSearch,
} from "../lib/navigationOrigin";
import { setWorkRouteSentryContext } from "../lib/sentry";
import {
  assetIdSchema,
  type CustomerId,
  customerIdSearchSchema,
  type FacilityId,
  facilityIdSearchSchema,
} from "../lib/workIds";
import { getWorkScopeSearch, type WorkSearch } from "../lib/workSearch";
import "../styles.css";

type RouteWorkSearch = WorkSearch & {
  assetSearch?: AssetTreeSearch["assetSearch"];
  /** @deprecated Superseded by `assetKind`. Retained for back-compat reads only. */
  assetType?: AssetTreeSearch["assetType"];
  assetKind?: AssetTreeSearch["assetKind"];
  assetSort?: AssetTreeSearch["assetSort"];
  assetSortDir?: AssetTreeSearch["assetSortDir"];
  assetSelectedId?: AssetTreeSearch["assetSelectedId"];
  assetCollapsedIds?: AssetTreeSearch["assetCollapsedIds"];
  assetOpenId?: AssetTreeSearch["assetOpenId"];
  customer?: CustomerId;
  facility?: FacilityId;
} & NavigationOriginSearch;

function WorkLayoutContent({ pathname }: { pathname: string }) {
  const navigate = useNavigate();
  const { isError, isPending, selectedCustomer, selectedFacility } =
    useWorkScope();
  const previousRole = useRef(selectedFacility?.role);
  const module = moduleForPath(pathname);
  const isDenied = Boolean(
    !isPending &&
      selectedFacility &&
      module &&
      !canAccessWebNavigationModule(selectedFacility, module),
  );
  const isLandingRedirect = Boolean(
    !isPending && selectedFacility && pathname === "/",
  );

  useEffect(() => {
    if (isPending || !selectedFacility) return;
    const redirect = isDenied
      ? fallbackPathForRole(selectedFacility)
      : pathname === "/"
        ? defaultPathForRole(selectedFacility)
        : redirectForRoleChange(
            pathname,
            previousRole.current,
            selectedFacility,
          );
    previousRole.current = selectedFacility.role;
    if (redirect && redirect !== pathname) {
      void navigate({
        to: redirect,
        replace: true,
        search:
          selectedCustomer && selectedFacility
            ? getWorkScopeSearch(selectedCustomer, selectedFacility)
            : {},
      });
    }
  }, [
    isDenied,
    isPending,
    pathname,
    navigate,
    selectedCustomer,
    selectedFacility,
  ]);

  return (
    <AppShell>
      <WorkHeader />
      <WorkSidebar pathname={pathname} />
      {pathname === "/profile" ? (
        <Outlet />
      ) : isPending ? (
        <AppShellContent pending>
          <Text tone="subtle">Checking Work access…</Text>
        </AppShellContent>
      ) : isDenied && selectedFacility ? (
        <AppShellContent>
          <Stack gap={16}>
            <Title as="h1" size="lg">
              Access denied
            </Title>
            <Text tone="subtle">
              You don&apos;t have permission to access this Work page with your
              current role.
            </Text>
            <Button
              render={
                <Link
                  search={
                    selectedCustomer && selectedFacility
                      ? getWorkScopeSearch(selectedCustomer, selectedFacility)
                      : {}
                  }
                  to={defaultPathForRole(selectedFacility)}
                />
              }
              size="sm"
              variant="outline"
            >
              Go to an available Work page
            </Button>
          </Stack>
        </AppShellContent>
      ) : isLandingRedirect ? (
        <AppShellContent pending>
          <Text tone="subtle">Opening your default Work page…</Text>
        </AppShellContent>
      ) : selectedFacility ? (
        <Outlet />
      ) : (
        <AppShellContent>
          <Text tone="subtle">
            {isError
              ? "Unable to load your Work access."
              : "No accessible Work facilities were found."}
          </Text>
        </AppShellContent>
      )}
    </AppShell>
  );
}
function WorkScopedLayout() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) =>
      state.resolvedLocation?.pathname ?? state.location.pathname,
  });

  useEffect(() => {
    setWorkRouteSentryContext(pathname);
  }, [pathname]);

  return (
    <WorkScopeProvider
      canonicalizeSearch={Boolean(search.customer || search.facility)}
      customerId={search.externalCustomerId ?? search.customer}
      facilityId={search.facilityId ?? search.facility}
      onSelectionChange={(selection, options) => {
        if (pathname.startsWith("/assets/")) {
          const assetId = assetIdSchema.safeParse(
            pathname.slice("/assets/".length),
          );
          if (assetId.success) {
            void navigate({
              params: { assetId: assetId.data },
              replace: options?.replace,
              search: selection,
              to: "/assets/$assetId",
            });
          }
          return;
        }
        if (
          pathname.startsWith("/procedures/") &&
          pathname !== "/procedures/builder"
        ) {
          const procedureId = pathname.slice("/procedures/".length);
          void navigate({
            params: { procedureId },
            replace: options?.replace,
            search: selection,
            to: "/procedures/$procedureId",
          });
          return;
        }
        const to = pathname.startsWith("/work-orders")
          ? "/work-orders"
          : pathname === "/requests"
            ? "/requests"
            : pathname === "/assets"
              ? "/assets"
              : pathname === "/team-users"
                ? "/team-users"
                : pathname === "/documents" ||
                    pathname.startsWith("/documents/")
                  ? "/documents"
                  : pathname === "/procedures" ||
                      pathname === "/procedures/builder"
                    ? pathname
                    : pathname === "/settings" ||
                        pathname.startsWith("/settings/")
                      ? "/settings/general"
                      : "/";
        void navigate({
          replace: options?.replace,
          search: selection,
          to,
        });
      }}
    >
      <WorkLayoutContent pathname={pathname} />
    </WorkScopeProvider>
  );
}

function WorkLayout() {
  return (
    <WorkAccessGate>
      <WorkScopedLayout />
    </WorkAccessGate>
  );
}

export function validateWorkRouteSearch(
  search: Record<string, unknown>,
): RouteWorkSearch {
  const result: RouteWorkSearch = {};
  const externalCustomerId = customerIdSearchSchema.safeParse(
    search.externalCustomerId,
  );
  const facilityId = facilityIdSearchSchema.safeParse(search.facilityId);
  const legacyCustomer = customerIdSearchSchema.safeParse(search.customer);
  const legacyFacility = facilityIdSearchSchema.safeParse(search.facility);
  if (externalCustomerId.success) {
    result.externalCustomerId = externalCustomerId.data;
  }
  if (legacyCustomer.success) {
    result.customer = legacyCustomer.data;
  }
  if (facilityId.success) {
    result.facilityId = facilityId.data;
  }
  if (legacyFacility.success) {
    result.facility = legacyFacility.data;
  }
  const assetSearch = assetTreeSearchSchema.safeParse(search);
  if (assetSearch.success) {
    result.assetSearch = assetSearch.data.assetSearch;
    result.assetType = assetSearch.data.assetType;
    result.assetKind = assetSearch.data.assetKind;
    result.assetSort = assetSearch.data.assetSort;
    result.assetSortDir = assetSearch.data.assetSortDir;
    result.assetSelectedId = assetSearch.data.assetSelectedId;
    result.assetCollapsedIds = assetSearch.data.assetCollapsedIds;
    result.assetOpenId = assetSearch.data.assetOpenId;
  }
  // Origins written by older links are normalized to the generic keys; an
  // invalid origin is dropped rather than failing the route.
  const origin = parseNavigationOrigin(search);
  return origin ? { ...result, ...toOriginSearch(origin) } : result;
}

export const Route = createFileRoute("/_app")({
  component: WorkLayout,
  validateSearch: validateWorkRouteSearch,
});
